import type { PropsWithChildren, ReactNode } from "react";

import { cn } from "@tudigo-monorepo/core-tudigo-theme";

import { useComparator } from "../comparator.context";
import {
  ComparatorScrollerButtons,
  ComparatorScrollerFloatingButtons,
} from "./comparator-scroller-buttons";
import { ComparatorScrollerStickybar } from "./comparator-scroller-sticky-bar";
import { ComparatorScrollerProvider } from "./store/comparator-scroller.provider";
import { useComparatorScroller } from "./store/hooks/use-comparator-scroller";

type ComparatorScrollerProps = {
  scrollableItemWidth: number;
  bottomScrollButtons?: boolean;
  className?: string;
  maxScrollWidthDisabled?: boolean;
  scrollButtonsPositionType?: "default" | "floating";
  stickybar?:
    | false
    | {
        className?: string;
        children?: ReactNode;
      };
};

function ComparatorScroller({
  children,
  className,
  scrollableItemWidth,
  bottomScrollButtons = false,
  maxScrollWidthDisabled = false,
  scrollButtonsPositionType = "default",
  stickybar = false,
}: PropsWithChildren<ComparatorScrollerProps>) {
  const { selectionCount } = useComparator();

  return (
    <div className={cn("relative mt-6 flex flex-col", className)}>
      <ComparatorScrollerProvider
        scrollableItemWidth={scrollableItemWidth}
        maxScrollWidthDisabled={maxScrollWidthDisabled}
      >
        {scrollButtonsPositionType === "floating" && selectionCount > 0 && (
          <ComparatorScrollerFloatingButtons />
        )}

        {!!stickybar && (
          <ComparatorScrollerStickybar
            className={stickybar.className}
            scrollButtonsPositionType={scrollButtonsPositionType}
          >
            {stickybar.children}
          </ComparatorScrollerStickybar>
        )}

        <HorizontalScrollArea>{children}</HorizontalScrollArea>

        {scrollButtonsPositionType === "default" && bottomScrollButtons && (
          <ComparatorScrollerButtons className="lg:hidden" />
        )}
      </ComparatorScrollerProvider>
    </div>
  );
}

function HorizontalScrollArea({ children }: { children?: ReactNode }) {
  const { state, onScroll } = useComparatorScroller();

  return (
    <div
      ref={state.scrollerElementRef}
      data-comparator-scroller=""
      onScroll={onScroll}
      style={{ maxWidth: state.maxScrollWidth }}
      className={cn(
        "snap-x snap-mandatory overflow-x-hidden overflow-y-hidden",
        !!state.maxScrollWidth && "lg:mr-10 lg:self-end",
      )}
    >
      {children}
    </div>
  );
}

export { type ComparatorScrollerProps, ComparatorScroller };
